// Standalone Angular 20 app config — no NgModule.

import { ApplicationConfig, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { provideAppCheck } from '@angular/fire/app-check';
import { initializeAppCheck, ReCaptchaV3Provider, CustomProvider } from 'firebase/app-check';
import { getApp } from 'firebase/app';
import { FirebaseAppCheck as CapacitorAppCheck } from '@capacitor-firebase/app-check';
import { Capacitor } from '@capacitor/core';
import { provideMessaging, getMessaging } from '@angular/fire/messaging';
import { provideRemoteConfig, getRemoteConfig } from '@angular/fire/remote-config';
import { routes } from './app.routes';
import { authInterceptor } from './services/auth.interceptor';
import { environment } from '../environments/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideIonicAngular({ mode: 'md' }),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),
    // search code AppCheck_
    provideAppCheck(() => {
      if (Capacitor.isNativePlatform()) {

        // It's a 1-hour fallback in milliseconds. The @capacitor-firebase/app-check plugin's 
        // getToken() returns expireTimeMillis from the native token 
        // (your Firebase Console Android setting is x hours). The ?? means: use the real expiry if
        //  the plugin provides it, fall back to 1 hour if it's missing. 
        // It's just a safety net — in practice result.expireTimeMillis will always be present.

        return initializeAppCheck(getApp(), {
          provider: new CustomProvider({
            getToken: async () => {
              const result = await CapacitorAppCheck.getToken({ forceRefresh: false });
              return {
                token: result.token,
                expireTimeMillis: result.expireTimeMillis ?? Date.now() + 3600000,
              };
            },
          }),
          isTokenAutoRefreshEnabled: true,
        });
      }

      // For ReCaptchaV3Provider, Firebase manages the token TTL automatically from the server response
      //  — you set X hours in Firebase Console and isTokenAutoRefreshEnabled: true handles the refresh.
      //  No manual TTL needed in code, unlike the CustomProvider above where we explicitly construct 
      // the return object.
      return initializeAppCheck(getApp(), {
        provider: new ReCaptchaV3Provider(environment.recaptchaSiteKey),
        isTokenAutoRefreshEnabled: true,
        // why no time here
      });
    }),
    provideMessaging(() => getMessaging()),
    provideRemoteConfig(() => {
      const remoteConfig = getRemoteConfig();
      remoteConfig.settings.minimumFetchIntervalMillis = 600000; // 10 minutes
      remoteConfig.defaultConfig = {
        app_availability_mode: 'NORMAL',
      };
      return remoteConfig;
    }),
  ],
};

// provideMessaging needs entry for vapidKey in src/environments/environment.ts and
// firebase-messaging-sw.js